import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useModalEscape from './useModalEscape';

function normalizeViewerSource(value) {
  if (!value || typeof value !== 'string') return '';
  if (value.startsWith('data:image/')) return value;
  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return '';
  }
}

export function normalizePhotoViewerSources(sources) {
  return (Array.isArray(sources) ? sources : [sources])
    .map(normalizeViewerSource)
    .filter(Boolean);
}

export default function PhotoViewer({ open, sources, initialSlide = 0, onClose, t }) {
  const validSources = useMemo(() => normalizePhotoViewerSources(sources), [sources]);
  const safeInitialSlide = Math.min(Math.max(Number(initialSlide) || 0, 0), Math.max(validSources.length - 1, 0));
  const [viewer, setViewer] = useState({ slide: safeInitialSlide, zoom: 1, offsetX: 0, offsetY: 0 });
  const pinchRef = useRef({ distance: 0, zoom: 1 });
  const dragRef = useRef({ active: false, pointerId: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });

  useEffect(() => {
    if (!open) return;
    setViewer({ slide: safeInitialSlide, zoom: 1, offsetX: 0, offsetY: 0 });
  }, [open, safeInitialSlide]);

  const closeViewer = useCallback(() => {
    dragRef.current = { active: false, pointerId: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 };
    onClose?.();
  }, [onClose]);

  useModalEscape(open, closeViewer);

  const moveViewer = useCallback((direction) => {
    setViewer((current) => ({
      ...current,
      slide: (current.slide + direction + validSources.length) % validSources.length,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
    }));
  }, [validSources.length]);

  const zoomViewer = useCallback((delta) => {
    setViewer((current) => ({
      ...current,
      zoom: Math.min(4, Math.max(1, Number((current.zoom + delta).toFixed(2)))),
      offsetX: current.zoom + delta <= 1 ? 0 : current.offsetX,
      offsetY: current.zoom + delta <= 1 ? 0 : current.offsetY,
    }));
  }, []);

  function setViewerZoom(nextZoom) {
    const zoom = Math.min(4, Math.max(1, Number(nextZoom.toFixed(2))));
    setViewer((current) => ({
      ...current,
      zoom,
      offsetX: zoom <= 1 ? 0 : current.offsetX,
      offsetY: zoom <= 1 ? 0 : current.offsetY,
    }));
  }

  function moveViewerPan(offsetX, offsetY) {
    setViewer((current) => ({
      ...current,
      offsetX: current.zoom <= 1 ? 0 : offsetX,
      offsetY: current.zoom <= 1 ? 0 : offsetY,
    }));
  }

  function touchDistance(touches) {
    const [first, second] = touches;
    return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
  }

  function handleViewerWheel(event) {
    event.preventDefault();
    zoomViewer(event.deltaY < 0 ? 0.18 : -0.18);
  }

  function handleViewerTouchStart(event) {
    if (event.touches.length !== 2) return;
    dragRef.current.active = false;
    pinchRef.current = {
      distance: touchDistance(event.touches),
      zoom: viewer.zoom,
    };
  }

  function handleViewerTouchMove(event) {
    if (event.touches.length !== 2 || !pinchRef.current.distance) return;
    event.preventDefault();
    setViewerZoom(pinchRef.current.zoom * (touchDistance(event.touches) / pinchRef.current.distance));
  }

  function handleViewerTouchEnd(event) {
    if (event.touches.length < 2) {
      pinchRef.current = { distance: 0, zoom: viewer.zoom };
    }
  }

  function handleViewerPointerDown(event) {
    if (viewer.zoom <= 1 || event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: viewer.offsetX,
      offsetY: viewer.offsetY,
    };
  }

  function handleViewerPointerMove(event) {
    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    moveViewerPan(
      drag.offsetX + event.clientX - drag.startX,
      drag.offsetY + event.clientY - drag.startY,
    );
  }

  function handleViewerPointerUp(event) {
    if (dragRef.current.pointerId === event.pointerId) {
      dragRef.current.active = false;
      dragRef.current.pointerId = null;
    }
  }

  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'ArrowLeft' && validSources.length > 1) moveViewer(-1);
      if (event.key === 'ArrowRight' && validSources.length > 1) moveViewer(1);
      if (event.key === '+' || event.key === '=') zoomViewer(0.25);
      if (event.key === '-') zoomViewer(-0.25);
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, validSources.length, moveViewer, zoomViewer]);

  if (!open || validSources.length === 0) return null;

  return (
    <div className="ct-photo-viewer" role="presentation" onMouseDown={closeViewer}>
      <div
        className="ct-photo-viewer-stage"
        role="dialog"
        aria-modal="true"
        aria-label={t('tryonGallery.viewerLabel')}
        onMouseDown={(event) => event.stopPropagation()}
        onWheel={handleViewerWheel}
        onTouchStart={handleViewerTouchStart}
        onTouchMove={handleViewerTouchMove}
        onTouchEnd={handleViewerTouchEnd}
        onPointerDown={handleViewerPointerDown}
        onPointerMove={handleViewerPointerMove}
        onPointerUp={handleViewerPointerUp}
        onPointerCancel={handleViewerPointerUp}
      >
        <div className="ct-photo-viewer-topbar">
          <span>{viewer.slide + 1} / {validSources.length}</span>
          <div>
            <button type="button" onClick={() => zoomViewer(-0.25)} aria-label={t('tryonGallery.zoomOut')}>
              <i className="ph ph-minus"></i>
            </button>
            <button type="button" onClick={() => zoomViewer(0.25)} aria-label={t('tryonGallery.zoomIn')}>
              <i className="ph ph-plus"></i>
            </button>
            <button type="button" onClick={closeViewer} aria-label={t('aiDialog.close')}>
              <i className="ph ph-x"></i>
            </button>
          </div>
        </div>
        {validSources.length > 1 ? (
          <button type="button" className="ct-photo-viewer-nav prev" onClick={() => moveViewer(-1)} aria-label={t('common.previous')}>
            <i className="ph ph-arrow-left"></i>
          </button>
        ) : null}
        <img
          src={validSources[viewer.slide]}
          alt=""
          className={viewer.zoom > 1 ? 'is-zoomed' : ''}
          draggable="false"
          style={{ transform: `translate(${viewer.offsetX}px, ${viewer.offsetY}px) scale(${viewer.zoom})` }}
        />
        {validSources.length > 1 ? (
          <button type="button" className="ct-photo-viewer-nav next" onClick={() => moveViewer(1)} aria-label={t('common.next')}>
            <i className="ph ph-arrow-right"></i>
          </button>
        ) : null}
      </div>
    </div>
  );
}
